// Global Instructions Rule Applied! - 2026-02-02

import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ConfigProvider, theme } from 'antd'
import { ToastContainer } from 'react-toastify'
import './core/utils/toastDefaults.ts' // error toasts stay until dismissed
import { Providers } from './state_manager/providers.tsx'
import { AuthGuard } from './core/auth/AuthGuard.jsx'
import { DrawerProvider } from './core/contexts/DrawerContext.jsx'
import { TabManagerProvider } from './core/app-layout/lib/TabManagerContext.tsx'
import MainTabContainer from './core/app-layout/MainTabContainer.tsx'
import { useIsMobile } from './core/hooks/useIsMobile.ts'
import { useSelector } from 'react-redux'
import { readStoredAreaKey } from './app_modules/global_configs/appPreferences.ts'
import { resolveAreaDefaultHref } from './ui_configs/left_menu.ts'
import { TOAST_AUTO_CLOSE, TOAST_POSITION } from './core/components/FormTable/constants.ts'

import 'react-toastify/dist/ReactToastify.css'

// Import login page (only one not using tabs)
import LoginPage from './app_modules/login/index.jsx'

// Ant Design theme configuration (owner-approved scheme, 2026-08-12).
// One primary: the brand blue of the navbar gradient (#4C90E9 → #2887D0 →
// #1E6DB8); AntD derives hover/active from it. Semantic tokens align with
// what Tags/danger buttons already render, so no visual churn there.
const READABLE_DISABLED_INK = 'rgba(0, 0, 0, 0.65)'
const antdTheme = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: '#4C90E9',
    colorInfo: '#4C90E9',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    borderRadius: 6,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  components: {
    Layout: {
      siderBg: '#fff',
      triggerBg: '#fff'
    },
    Menu: {
      itemBg: 'transparent'
    },
    // Disabled/read-only FIELD values (e.g. a Booked transaction form) keep
    // the grey field background but use readable secondary-text ink. Scoped
    // to input components only: applied globally it also hit disabled
    // BUTTONS, which then looked enabled (a disabled Book button read as
    // clickable). Buttons keep AntD's stock 25% disabled ink.
    Input: { colorTextDisabled: READABLE_DISABLED_INK },
    InputNumber: { colorTextDisabled: READABLE_DISABLED_INK },
    Select: { colorTextDisabled: READABLE_DISABLED_INK },
    DatePicker: { colorTextDisabled: READABLE_DISABLED_INK },
    Checkbox: { colorTextDisabled: READABLE_DISABLED_INK },
    Radio: { colorTextDisabled: READABLE_DISABLED_INK }
  }
}

/**
 * Root landing. Desktop goes to the deals pipeline; mobile goes to the mobile Home
 * (recents + pipeline snapshot + browse/search entry points). This is the launch target
 * for the installed PWA (start_url "/") and the post-login redirect, so it must resolve to
 * a usable screen on a phone.
 */
function RootLanding(): React.JSX.Element {
  const isMobile = useIsMobile()
  const globalConfigs = useSelector((state: any) => state.globalConfigs)

  // Desktop lands in the user's stored area, at that area's default item.
  // A hardcoded '/deals/pipeline' would defeat the stored preference entirely:
  // it is an exact-href menu item in Fund Management only, so every launch
  // through '/' (post-login redirect and the PWA start_url) would resolve that
  // area and overwrite whatever the user had chosen. RootLanding sits inside
  // AuthGuard, so menu cfg and the preference are already loaded here.
  const storedAreaKey = readStoredAreaKey(globalConfigs)
  const desktopHref = resolveAreaDefaultHref(storedAreaKey, globalConfigs)

  return <Navigate to={isMobile ? '/home' : desktopHref} replace />
}

function App(): React.JSX.Element {
  return (
    <ConfigProvider theme={antdTheme}>
      <Providers>
        <Router>
          <TabManagerProvider>
            <DrawerProvider>
              <Routes>
                {/* Public routes */}
                <Route path='/login' element={<LoginPage />} />

                {/* Root redirect - handle OAuth callback */}
                <Route
                  path='/'
                  element={
                    <AuthGuard>
                      <RootLanding />
                    </AuthGuard>
                  }
                />

                {/* All authenticated routes use the main tab container */}
                <Route
                  path='/*'
                  element={
                    <AuthGuard>
                      <MainTabContainer />
                    </AuthGuard>
                  }
                />
              </Routes>

              {/* Toast Container for notifications */}
              <ToastContainer
                position={TOAST_POSITION}
                autoClose={TOAST_AUTO_CLOSE.DEFAULT}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme='light'
              />
            </DrawerProvider>
          </TabManagerProvider>
        </Router>
      </Providers>
    </ConfigProvider>
  )
}

export default App
